import { cubic, length, resample, type Pt } from "./ribbon";
import { braneThread, knotPose, knotScene, THREAD_MEET } from "./figures";
import { stripFor, type Line } from "./weave";

/*
  The landing's one thread, as geometry. It comes in from the very top of the
  page and runs on down it: past the ends of the hero headline's lines through
  the open paper (never across a word), down the margin, down the
  gap between the comparison's last two columns, into the String figure where
  theirs collides with it, through the Brane's ring (over its near rim, under its
  far one, coming apart into three on the far side), through the Knot as one of its strands, round the
  Loop and round again, and past the closing headline the way it came past the
  first, off the very bottom of the page. One strip, in at the top and out at
  the bottom.

  `route` turns measured boxes into pieces; `lay` flattens the pieces into one
  evenly spaced line with a width, a gate and flags per point. A point's gate
  is how far down the page the reader must have scrolled before the thread is
  drawn that far, so it grows down the page as the page is read, never ahead of
  where it is going. Pure, so it is tested without a browser.
*/

export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

const right = (b: Box) => b.left + b.width;
const bottom = (b: Box) => b.top + b.height;

export interface Room {
  section: Box;
  figure: Box;
  text: Box;
}

export interface ThreadLayout {
  /** The page's width; every coordinate is in the page's own pixels. */
  width: number;
  /** The page's height: the thread runs off its bottom edge. */
  height: number;
  /** The clear paper either side of the content. */
  margin: number;
  /** The hero headline's lines; the thread goes round them, never across them. */
  hero: Line[];
  /** The closing headline's lines. */
  close: Line[];
  /** The comparison's columns: when they stand side by side the thread runs down the gap between the last two. */
  compare: Box[];
  string: Room;
  /** The Brane, on the bare sheet: the thread goes through its ring. */
  brane?: Room;
  knot: Room;
  loop: Room;
}

interface Piece {
  pts: Pt[];
  /** Width at the start and the end; it changes evenly along the piece. */
  width: [number, number];
  /** The scroll range over which the piece is drawn. Without one, a point is drawn once the reader has scrolled to it. */
  gate?: [number, number];
  /** Drawn over the type and the figures, not under: where the thread crosses in front. */
  over?: boolean;
  /** Fractions of the piece drawn over, flat (no lift), when only part of it is: a weave, where paper lies on paper. */
  overAt?: [number, number][];
  /** The reader can take it in hand here. */
  grip?: boolean;
}

/** Points are this far apart along the laid thread. */
export const STEP = 6;

const unit = (a: Pt, b: Pt): Pt => {
  const d = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
  return [(b[0] - a[0]) / d, (b[1] - a[1]) / d];
};
const DOWN: Pt = [0, 1];
const LEFT: Pt = [-1, 0];
const RIGHT: Pt = [1, 0];

/** A smooth run from `a`, leaving along `da`, to `b`, arriving along `db`. */
function link(a: Pt, da: Pt, b: Pt, db: Pt): Pt[] {
  const d = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const k = d * 0.42;
  // Sampled finely enough that the laid strip shows no facets.
  return cubic(a, [a[0] + da[0] * k, a[1] + da[1] * k], [b[0] - db[0] * k, b[1] - db[1] * k], b, Math.max(24, Math.ceil(d / 3)));
}

const line = (a: Pt, b: Pt): Pt[] => [a, b];

/* ── The Loop's coil ─────────────────────────────────────────────────────── */

/** Where the strip goes round, in the figure's 400 box: from about eleven o'clock, clockwise. */
const COIL_FROM = (-104 * Math.PI) / 180;
const COIL_OUTER = 118;
const COIL_INNER = 72;
const COIL_CROSS = Math.PI / 3;
/** Out past the ring, on its way to the next room. */
const COIL_AWAY = 168;

function arc(from: number, to: number, r0: number, r1: number): Pt[] {
  const n = Math.max(8, Math.ceil(((to - from) * Math.max(r0, r1)) / 4));
  const pts: Pt[] = [];
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    const s = t * t * (3 - 2 * t);
    const r = r0 + (r1 - r0) * s;
    const a = COIL_FROM + from + (to - from) * t;
    pts.push([200 + Math.cos(a) * r, 200 + Math.sin(a) * r]);
  }
  return pts;
}

/** Round once, across its own start into a second lap inside the first, round again, and across the first lap out. */
export function coil(): { laps: Pt[][]; over: boolean[] } {
  const once = 2 * Math.PI;
  const again = once + COIL_CROSS + 1.5 * Math.PI;
  return {
    laps: [
      arc(0, once, COIL_OUTER, COIL_OUTER),
      arc(once, once + COIL_CROSS, COIL_OUTER, COIL_INNER),
      arc(once + COIL_CROSS, again, COIL_INNER, COIL_INNER),
      arc(again, again + COIL_CROSS, COIL_INNER, COIL_AWAY),
    ],
    over: [false, true, false, true],
  };
}

/* ── Round a headline ────────────────────────────────────────────────────── */

/**
 * A smooth line through `pts`, finely sampled: centripetal Catmull-Rom, which
 * neither overshoots nor loops where the points are unevenly spaced, so a long
 * run into a short turn stays a clean turn.
 */
function through(pts: Pt[]): Pt[] {
  const out: Pt[] = [];
  const n = pts.length;
  const mix = (a: Pt, b: Pt, ta: number, tb: number, t: number): Pt => {
    const span = tb - ta || 1;
    return [((tb - t) * a[0] + (t - ta) * b[0]) / span, ((tb - t) * a[1] + (t - ta) * b[1]) / span];
  };
  const knot = (a: Pt, b: Pt) => Math.max(1e-3, Math.sqrt(Math.hypot(b[0] - a[0], b[1] - a[1])));
  for (let i = 0; i < n - 1; i++) {
    const p1 = pts[i];
    const p2 = pts[i + 1];
    // Beyond the ends, the line carries straight on.
    const p0: Pt = i > 0 ? pts[i - 1] : [2 * p1[0] - p2[0], 2 * p1[1] - p2[1]];
    const p3: Pt = i < n - 2 ? pts[i + 2] : [2 * p2[0] - p1[0], 2 * p2[1] - p1[1]];
    const t0 = 0;
    const t1 = t0 + knot(p0, p1);
    const t2 = t1 + knot(p1, p2);
    const t3 = t2 + knot(p2, p3);
    const steps = Math.max(8, Math.ceil(Math.hypot(p2[0] - p1[0], p2[1] - p1[1]) / 3));
    for (let k = i ? 1 : 0; k <= steps; k++) {
      const t = t1 + ((t2 - t1) * k) / steps;
      const a1 = mix(p0, p1, t0, t1, t);
      const a2 = mix(p1, p2, t1, t2, t);
      const a3 = mix(p2, p3, t2, t3, t);
      out.push(mix(mix(a1, a2, t0, t2, t), mix(a2, a3, t1, t3, t), t1, t2, t));
    }
  }
  return out;
}

/** Clear of the ends of `lines` (and of nothing else) by `pad`. */
const clearOfEnds = (lines: Line[], pad: number) => Math.max(...lines.map((l) => l.right)) + pad;

/** Any point that has come within reach of a line's type is moved out to the right of it: the guarantee behind `rag`. */
function clearOf(pts: Pt[], lines: Line[], w: number): Pt[] {
  const gap = w / 2 + 4;
  return pts.map(([x, y]) => {
    let nx = x;
    for (const l of lines) {
      if (y + gap > l.top && y - gap < l.bottom && nx + gap > l.left && nx - gap < l.right) nx = l.right + gap;
    }
    return [nx, y] as Pt;
  });
}

/* ── The route ───────────────────────────────────────────────────────────── */

/** The figure's 400 box on the page. */
const inFigure = (room: Room) => {
  const s = room.figure.width / 400;
  return { s, at: ([x, y]: Pt): Pt => [room.figure.left + x * s, room.figure.top + y * s] };
};

/** Side by side: the figure and the text share rows, so the thread cannot go down between them and the edge without crossing one. */
const besideText = (room: Room) =>
  (right(room.figure) <= room.text.left || room.figure.left >= right(room.text)) &&
  room.figure.top < bottom(room.text) &&
  room.text.top < bottom(room.figure);

/** The clear band at the top of a room, above its first line and its figure. */
const band = (room: Room) => room.section.top + (Math.min(room.text.top, room.figure.top) - room.section.top) / 2;

export function route(layout: ThreadLayout): Piece[] {
  const { width: W, margin, hero, close } = layout;
  const xR = W - margin / 2;
  const xL = margin / 2;
  // In the margin the thread is as wide as the margin allows, never wider than the headline's strip.
  const wM = Math.min(stripFor(hero[0].bottom - hero[0].top), Math.max(12, margin * 0.8));
  const turn = Math.max(40, Math.min(120, margin * 1.1));
  const pieces: Piece[] = [];
  const add = (pts: Pt[], w0: number, w1 = w0, extra: Omit<Piece, "pts" | "width"> = {}) =>
    pieces.push({ pts, width: [w0, w1], ...extra });
  const last = (): Pt => {
    const p = pieces[pieces.length - 1].pts;
    return p[p.length - 1];
  };

  // The hero: in from the very top of the page, down through the open paper past the ends of the
  // headline's lines (never across a word), and into the margin.
  const lh = hero[0].bottom - hero[0].top;
  const hs = stripFor(lh);
  {
    const pad = lh * 0.22 + hs / 2;
    const clear = Math.min(xR, clearOfEnds(hero, pad));
    const x = Math.min(xR, clear + lh * 0.6);
    const mid = hero[Math.floor((hero.length - 1) / 2)];
    const foot = hero[hero.length - 1];
    const end: Pt = [xR, foot.bottom + lh * 0.25];
    const way: Pt[] = [[x, -lh * 0.6], [x, hero[0].top], [clear, (mid.top + mid.bottom) / 2], end, [xR, end[1] + lh * 0.6]];
    add(clearOf(through(way), hero, hs), hs, hs, { gate: [0, 0], grip: true });
    // Narrowing to the margin's width.
    add(line(last(), [xR, last()[1] + 160]), hs, wM);
  }

  // Down the margin, and down the gap between the comparison's last two columns when they stand side by side.
  const cols = layout.compare;
  if (cols.length > 1 && Math.abs(cols[0].top - cols[cols.length - 1].top) < 2) {
    const seam = right(cols[cols.length - 2]);
    const top = cols[0].top;
    add(line(last(), [xR, Math.max(last()[1], top - turn * 2.4)]), wM);
    add(link(last(), DOWN, [seam, top], DOWN), wM);
    add(line([seam, top], [seam, bottom(cols[0])]), wM);
  }

  // The String: yours swings in from the margin, theirs collides with it at the meeting point, and yours swings out.
  {
    const { s, at } = inFigure(layout.string);
    const meet = at(THREAD_MEET);
    const into: Pt = [xR, layout.string.figure.top];
    const out: Pt = [xR, layout.string.figure.top + 400 * s];
    const ws = 30 * s;
    if (last()[0] === xR) {
      // From the margin: swing in to the meeting point.
      add(line(last(), into), wM);
      add(cubic(into, [into[0], into[1] + 110 * s], [meet[0], meet[1] - 130 * s], meet, 120), wM, ws, { grip: true });
    } else {
      // From the comparison's gap, already above the figure: drop straight down into it.
      const from = last();
      const drop = meet[1] - from[1];
      add(cubic(from, [from[0], from[1] + drop * 0.45], [meet[0], meet[1] - drop * 0.4], meet, 160), wM, ws, { grip: true });
    }
    add(cubic(meet, [meet[0], meet[1] + 130 * s], [out[0], out[1] - 110 * s], out, 120), ws, wM, { grip: true });
  }

  // The Brane: down the margin, in over the top of the ring and across it from the left (over the near
  // rim, under the far one, where more strips peel off it), and back out to the margin.
  if (layout.brane) {
    const { s, at } = inFigure(layout.brane);
    const through = braneThread();
    const page = through.pts.map(at);
    const n = page.length - 1;
    const wb = 30 * s;
    add(line(last(), [xR, Math.max(last()[1], layout.brane.figure.top - turn)]), wM);
    add(link(last(), DOWN, page[0], unit(page[0], page[1])), wM, wb, { grip: true });
    add(page, wb, wb, { overAt: through.over });
    add(link(page[n], unit(page[n - 1], page[n]), [xR, page[n][1] + turn * 1.5], DOWN), wb, wM);
  }

  // The Knot: in from the right as its middle strand, woven over and under, and out to the left margin.
  {
    const room = layout.knot;
    const { s, at } = inFigure(room);
    const strand = knotPose()[1].slice().reverse().map(at);
    const n = strand.length - 1;
    const kIn = strand[0];
    const kOut = strand[n];
    const dIn = unit(strand[0], strand[1]);
    const dOut = unit(strand[n - 1], strand[n]);
    const wk = 38 * s;
    if (besideText(room) && right(room.figure) <= room.text.left) {
      // The figure is on the left and the text on the right: cross above the text, then down the gutter between them.
      const y = band(room);
      const gap = (right(room.figure) + room.text.left) / 2;
      add(line(last(), [xR, y - turn]), wM);
      add(link([xR, y - turn], DOWN, [xR - turn, y], LEFT), wM);
      add(line([xR - turn, y], [gap + turn, y]), wM);
      add(link([gap + turn, y], LEFT, [gap, y + turn], DOWN), wM);
      add(link([gap, y + turn], DOWN, kIn, dIn), wM, wk);
    } else {
      add(line(last(), [xR, Math.max(last()[1], kIn[1] - turn * 2)]), wM);
      add(link(last(), DOWN, kIn, dIn), wM, wk);
    }
    // Over the strands where its row and their column add up even, as the rest of the weave goes (figures.ts).
    const overAt = knotScene.patches
      .filter((p) => p.strip === 1)
      .map((p) => [1 - p.to / n, 1 - p.from / n] as [number, number]);
    add(strand, wk, wk, { gate: [kIn[1] - 200, kIn[1] + 160], overAt });
    add(link(kOut, dOut, [xL, kOut[1] + turn * 1.5], DOWN), wk, wM);
  }

  // The Loop: in along the top of the ring, round once, across its start, round again, and out across the first lap.
  {
    const room = layout.loop;
    const { s, at } = inFigure(room);
    const { laps, over } = coil();
    const page = laps.map((lap) => lap.map(at));
    const start = page[0][0];
    const dStart = unit(page[0][0], page[0][1]);
    const wl = 36 * s;
    if (besideText(room) && room.figure.left >= right(room.text)) {
      // The figure is on the right and the text on the left: cross above the text.
      const y = band(room);
      add(line(last(), [xL, y - turn]), wM);
      add(link([xL, y - turn], DOWN, [xL + turn, y], RIGHT), wM);
      add(line([xL + turn, y], [room.figure.left - turn, y]), wM);
      add(link([room.figure.left - turn, y], RIGHT, start, dStart), wM, wl);
    } else {
      add(line(last(), [xL, Math.max(last()[1], start[1] - turn * 2)]), wM);
      add(link(last(), DOWN, start, dStart), wM, wl);
    }
    // Wound as the page is read: round once over the figure's first half on screen, round again over the rest.
    const total = page.reduce((sum, lap) => sum + length(lap), 0);
    let g = start[1] - room.figure.height * 0.15;
    const span = room.figure.height * 1.1;
    page.forEach((lap, i) => {
      const next = g + (span * length(lap)) / total;
      add(lap, wl, wl, { gate: [g, next], over: over[i], grip: !over[i] });
      g = next;
    });
    const away = page[page.length - 1];
    const end = away[away.length - 1];
    add(link(end, unit(away[away.length - 2], end), [xR, end[1] + turn * 2], DOWN), wl, wM);
  }

  // The close, the hero's mirror: in from the margin, swaying past the ends of the closing headline's
  // lines (never across a word), back to the margin, and out off the very bottom of the page.
  {
    const ch = close[0].bottom - close[0].top;
    const cs = stripFor(ch);
    const pad = ch * 0.22 + cs / 2;
    const clear = Math.min(xR, clearOfEnds(close, pad));
    const mid = close[Math.floor((close.length - 1) / 2)];
    const foot = close[close.length - 1];
    add(line(last(), [xR, Math.max(last()[1], close[0].top - turn * 2.5)]), wM);
    const way: Pt[] = [
      last(),
      [xR, close[0].top - ch * 0.6],
      [clear, (mid.top + mid.bottom) / 2],
      [xR, foot.bottom + ch * 0.6],
      // Held straight down the margin from here, so the long last run does not bow.
      [xR, foot.bottom + ch * 1.4],
      [xR, Math.max(foot.bottom + ch * 2.2, layout.height + ch)],
    ];
    add(clearOf(through(way), close, Math.max(wM, cs)), wM, cs, { grip: true });
  }
  return pieces;
}

/* ── Laying it ───────────────────────────────────────────────────────────── */

export interface Laid {
  pts: Pt[];
  widths: number[];
  /** Distance along the thread to each point. */
  arc: number[];
  /** Never decreasing: the scroll a point waits for. */
  gate: number[];
  /** Stretches of the thread, by distance along it, drawn over the page rather than under; lifted ones cast a shadow. */
  over: { from: number; to: number; lift: boolean }[];
  /** Stretches the reader can take in hand. */
  grip: [number, number][];
  total: number;
}

export function lay(pieces: Piece[]): Laid {
  const pts: Pt[] = [];
  const widths: number[] = [];
  const gate: number[] = [];
  const arc: number[] = [];
  const over: Laid["over"] = [];
  const grip: [number, number][] = [];
  let total = 0;
  let floor = -Infinity;
  for (const piece of pieces) {
    const len = length(piece.pts);
    if (len < 0.5) continue;
    const n = Math.max(2, Math.ceil(len / STEP) + 1);
    const even = resample(piece.pts, n);
    const from = total;
    even.forEach((p, i) => {
      const t = i / (n - 1);
      if (i === 0 && pts.length) return; // the seam: same point as the last piece's end
      if (pts.length) total += Math.hypot(p[0] - pts[pts.length - 1][0], p[1] - pts[pts.length - 1][1]);
      pts.push(p);
      arc.push(total);
      widths.push(piece.width[0] + (piece.width[1] - piece.width[0]) * t);
      const g = piece.gate ? piece.gate[0] + (piece.gate[1] - piece.gate[0]) * t : p[1];
      floor = Math.max(floor, g);
      gate.push(floor);
    });
    if (piece.over) over.push({ from, to: total, lift: true });
    for (const [a, b] of piece.overAt ?? []) over.push({ from: from + a * len, to: from + b * len, lift: false });
    if (piece.grip) grip.push([from, total]);
  }
  return { pts, widths, arc, gate, over, grip, total };
}

/** How far along the thread is drawn when the reader has scrolled to `y`. */
export function reach(laid: Laid, y: number): number {
  const { gate, arc } = laid;
  if (!gate.length || y < gate[0]) return 0;
  let lo = 0;
  let hi = gate.length - 1;
  if (y >= gate[hi]) return laid.total;
  // The last point whose gate has opened.
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (gate[mid] <= y) lo = mid;
    else hi = mid - 1;
  }
  const span = gate[lo + 1] - gate[lo];
  const t = span > 0 ? (y - gate[lo]) / span : 0;
  return arc[lo] + (arc[lo + 1] - arc[lo]) * t;
}

/** The stretch of the thread between two distances along it: points and widths, ends cut where they fall. */
export function stretch(laid: Laid, from: number, to: number, pts: readonly Pt[] = laid.pts): { pts: Pt[]; widths: number[] } {
  const { arc, widths } = laid;
  const a = Math.max(0, from);
  const b = Math.min(laid.total, to);
  if (b - a < 1) return { pts: [], widths: [] };
  const at = (d: number) => {
    let lo = 0;
    let hi = arc.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (arc[mid] <= d) lo = mid;
      else hi = mid - 1;
    }
    const j = Math.min(lo + 1, arc.length - 1);
    const t = arc[j] > arc[lo] ? (d - arc[lo]) / (arc[j] - arc[lo]) : 0;
    return {
      i: lo,
      p: [pts[lo][0] + (pts[j][0] - pts[lo][0]) * t, pts[lo][1] + (pts[j][1] - pts[lo][1]) * t] as Pt,
      w: widths[lo] + (widths[j] - widths[lo]) * t,
    };
  };
  const s = at(a);
  const e = at(b);
  const outPts: Pt[] = [s.p];
  const outW: number[] = [s.w];
  for (let i = s.i + 1; i <= e.i; i++) {
    outPts.push(pts[i]);
    outW.push(widths[i]);
  }
  outPts.push(e.p);
  outW.push(e.w);
  return { pts: outPts, widths: outW };
}

/**
 * The stretch of the thread, up to `to`, that comes between `top` and `bottom`
 * down the page: what is on screen, and the point either side of it so the cut
 * ends fall off screen. Null when none of it does.
 */
export function within(laid: Laid, to: number, top: number, bottom: number, pts: readonly Pt[] = laid.pts): [number, number] | null {
  const { arc } = laid;
  let first = -1;
  let last = -1;
  let tip = -1;
  for (let i = 0; i < arc.length && arc[i] <= to; i++) {
    tip = i;
    const y = pts[i][1];
    if (y < top || y > bottom) continue;
    if (first < 0) first = i;
    last = i;
  }
  if (first < 0) return null;
  return [first > 0 ? arc[first - 1] : 0, last < tip ? arc[last + 1] : to];
}

/** The page less its fields: the bare paper, as rectangles that do not overlap. */
export function bare(width: number, height: number, fields: readonly Box[]): Box[] {
  const ys = [...new Set([0, height, ...fields.flatMap((f) => [f.top, bottom(f)])])]
    .filter((y) => y >= 0 && y <= height)
    .sort((a, b) => a - b);
  const out: Box[] = [];
  for (let k = 0; k < ys.length - 1; k++) {
    const [top, end] = [ys[k], ys[k + 1]];
    const spans = fields
      .filter((f) => f.top <= top && bottom(f) >= end)
      .map((f) => [f.left, right(f)])
      .sort((a, b) => a[0] - b[0]);
    let x = 0;
    for (const [a, b] of spans) {
      if (a > x) out.push({ left: x, top, width: Math.min(a, width) - x, height: end - top });
      x = Math.max(x, b);
      if (x >= width) break;
    }
    if (x < width) out.push({ left: x, top, width: width - x, height: end - top });
  }
  return out;
}
